import { mountVueWithPinia, unmountVue } from '../vue/mountVue';
import { audienceLibrary, waitForDYObject } from '@utils/global-functions';
import { passJsonAsProp } from '@utils/ProductResources';
import {
  mountDYAudienceRecommendations,
  loadDYRecommendationSliders,
} from '@utils/dyResources';
import { hasConsent } from '@utils/readConsent';
import ProductGrid from '../vue/ProductCards/ProductGrid.vue';
import ProductSlider from '../vue/ProductCards/ProductSlider.vue';

const PRODUCT_LAYOUT_POLL_INTERVAL = 100;

let productLayoutOverridePromise = null;

/**
 * Polls for window.Brooklinen.productLayout, which an external system (e.g.
 * Dynamic Yield) sets asynchronously to run an A/B test between the
 * product-grid and product-slider layouts. Resolves with the override value
 * once it appears, or with null if it never appears before
 * window.Brooklinen.dyExpiration elapses, so the Liquid-rendered default is
 * used. Skips polling and resolves null immediately when the shopper has
 * denied targeting consent, since DY won't fire in that case. Memoized so
 * every module-collection instance on the page shares one poll.
 * @returns {Promise<'product-grid'|'product-slider'|null>}
 */
function waitForProductLayoutOverride() {
  if (productLayoutOverridePromise) return productLayoutOverridePromise;

  productLayoutOverridePromise = new Promise((resolve) => {
    if (!hasConsent('targeting')) {
      resolve(null);
      return;
    }

    const expiration = window.Brooklinen?.dyExpiration ?? 5000;
    let elapsed = 0;

    const intervalId = setInterval(() => {
      const productLayout = window.Brooklinen?.productLayout;
      if (
        productLayout === 'product-grid' ||
        productLayout === 'product-slider'
      ) {
        clearInterval(intervalId);
        resolve(productLayout);
        return;
      }

      elapsed += PRODUCT_LAYOUT_POLL_INTERVAL;
      if (elapsed >= expiration) {
        clearInterval(intervalId);
        resolve(null);
      }
    }, PRODUCT_LAYOUT_POLL_INTERVAL);
  });

  return productLayoutOverridePromise;
}

/**
 * A/B test override: swaps a module-collection element's custom element tag
 * and inner mount-point classes from the layout Liquid rendered to the one
 * window.Brooklinen.productLayout calls for, then mounts the matching Vue
 * component onto the resulting element. Both layouts render identical JSON
 * item data, so no other markup needs to change.
 * @param {HTMLElement} moduleElement - The rendered <product-grid> or <product-slider> element.
 * @param {'product-grid'|'product-slider'} fromLayout - The layout Liquid rendered.
 * @param {'product-grid'|'product-slider'} toLayout - The layout to render instead.
 */
function swapModuleLayout(moduleElement, fromLayout, toLayout) {
  const replacementElement = document.createElement(toLayout);

  [...moduleElement.attributes].forEach((attribute) => {
    replacementElement.setAttribute(attribute.name, attribute.value);
  });
  while (moduleElement.firstChild) {
    replacementElement.appendChild(moduleElement.firstChild);
  }

  const mountElement = replacementElement.querySelector(`.js-${fromLayout}`);
  mountElement?.classList.replace(`js-${fromLayout}`, `js-${toLayout}`);

  const jsonElement = replacementElement.querySelector(
    `.js-${fromLayout}-json`
  );
  jsonElement?.classList.replace(
    `js-${fromLayout}-json`,
    `js-${toLayout}-json`
  );

  moduleElement.replaceWith(replacementElement);

  if (toLayout === 'product-grid') {
    new ProductGrids(replacementElement);
  } else {
    new ProductSliders(replacementElement);
  }
}

/**
 * A/B test: called after a module-collection instance has already mounted
 * its Liquid-rendered default layout, so the initial render is never
 * blocked on Dynamic Yield. If window.Brooklinen.productLayout later
 * resolves to the opposite layout, unmounts the Vue app that was just
 * mounted and swaps the module over via swapModuleLayout.
 * @param {HTMLElement} context - The rendered <product-grid> or <product-slider> element.
 * @param {'product-grid'|'product-slider'} currentLayout - The layout that was just mounted.
 */
async function applyProductLayoutOverride(context, currentLayout) {
  const overrideLayout = await waitForProductLayoutOverride();
  const otherLayout =
    currentLayout === 'product-grid' ? 'product-slider' : 'product-grid';
  if (overrideLayout === otherLayout) {
    unmountVue(`.js-${currentLayout}`, context);
    swapModuleLayout(context, currentLayout, otherLayout);
  }
}

export class ProductGrids extends HTMLElement {
  constructor(context) {
    super();
    if (context) this.mountProductCards(context);
  }
  async mountProductCards(context) {
    //if the in page navigation module exists on a landing page or PLP, prevent auto scrolling to the top of the page on load
    if (!document.querySelector('.page-navigation')) window.scrollTo(0, 0);

    const app = context.querySelector('.js-product-grid');
    const jsonScript = context.querySelector('.js-product-grid-json');
    if (!app || !jsonScript) return;
    await passJsonAsProp(app, jsonScript.innerHTML);
    mountVueWithPinia('.js-product-grid', ProductGrid, context);

    //A/B test: swap to product-slider afterward if DY calls for it, without
    //blocking the mount above.
    if (context.dataset.htModule === 'collection') {
      applyProductLayoutOverride(context, 'product-grid');
    }
  }
}

//Call the DY choose endpoint once per page load, if there are DY recommendation sections on the page
document.addEventListener('DOMContentLoaded', function () {
  const dySliders = document.querySelectorAll(
    '[data-ht-module="dy-recommendation-slider"]'
  );
  if (dySliders.length > 0) {
    loadDYRecommendationSliders(dySliders);
  }
});
export class ProductSliders extends HTMLElement {
  constructor(context) {
    super();
    if (context) this.mountProductCards(context);
  }
  async mountProductCards(context) {
    const app = context.querySelector('.js-product-slider');
    //if the product slider is powered by DY audiences, wait for the audience DY data to come in, then grab the correct audience specific JSON to mount Vue.
    if (context.classList.contains('audience-strategies')) {
      waitForDYObject().then((obj) => {
        //Default to the low intent strategy when the user matches no audience,
        //or when DY never loaded (obj is null), so the slider still renders.
        const matchedAudience = obj
          ? audienceLibrary.filter((audience) =>
              obj.audiences.includes(audience.id)
            )?.[0]
          : null;
        const audienceId = matchedAudience?.index || 1;
        const recommendationData = context.querySelector(
          `.js-product-slider-json[data-audience="${audienceId}"]`
        );
        mountDYAudienceRecommendations(app, context, recommendationData);
      });
    } else if (!app.classList.contains('js-dy-product-slider')) {
      //mount product cards regularly
      const jsonScript = context.querySelector('.js-product-slider-json');
      if (!app || !jsonScript) return;
      await passJsonAsProp(app, jsonScript.innerHTML);
      mountVueWithPinia('.js-product-slider', ProductSlider, context);

      //A/B test: swap to product-grid afterward if DY calls for it, without
      //blocking the mount above. Scoped to the regular mount path since the
      //DY-audience and DY-recommendation sliders above render data shapes
      //swapModuleLayout doesn't know how to carry over.
      if (context.dataset.htModule === 'collection') {
        applyProductLayoutOverride(context, 'product-slider');
      }
    }
  }
}

customElements.define('product-grid', ProductGrids);
customElements.define('product-slider', ProductSliders);
